"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowUpRight, Search } from "lucide-react";
import { tools } from "@/config/tools";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname, withLocale } from "@/lib/i18n";
import { getSiteContent } from "@/lib/site-content";
import { getLocalizedTools } from "@/lib/tool-localization";

// Helper to assign variants
const getVariant = (index: number) => {
  // Fixed pattern: Grey (Left) -> Green (Middle) -> Dark (Right)
  const pattern = ['grey', 'green', 'dark'];
  return pattern[index % pattern.length];
};

export function AllToolsList() {
  const [searchQuery, setSearchQuery] = useState("");
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const content = getSiteContent(locale).toolsPage;
  const localizedTools = getLocalizedTools(tools, locale);

  const filteredTools = localizedTools.filter(tool => 
    tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Search Bar */}
      <div className="max-w-xl mx-auto mb-12 relative">
        <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
          <Search className="h-6 w-6 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder={content.searchPlaceholder}
          className="w-full h-14 pl-12 pr-6 rounded-[20px] border-2 border-positivus-dark text-lg focus:outline-none focus:ring-2 focus:ring-positivus-green focus:border-transparent transition-all shadow-[0_4px_0_0_#191A23]"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Results Grid */}
      {filteredTools.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTools.map((tool, index) => {
             const variant = getVariant(index);
             
             return (
              <Link 
                href={withLocale(locale, `/${tool.slug}`)}
                key={tool.id}
                className="block"
              >
                <div 
                  className={`
                    p-8 rounded-[45px] border border-positivus-dark flex flex-col h-[200px] relative overflow-hidden group transition-shadow hover:shadow-[0_5px_0_0_#191A23]
                    ${variant === 'grey' ? 'bg-positivus-grey' : 
                      variant === 'green' ? 'bg-positivus-green' : 'bg-positivus-dark'}
                  `}
                >
                  <div className="flex justify-between items-start z-10 w-full mb-4">
                     <h3 className={`text-xl font-medium px-3 py-1 rounded-[7px] inline-block w-fit
                       ${variant === 'grey' ? 'bg-black text-white' : ''}
                       ${variant === 'green' ? 'bg-white text-black' : ''}
                       ${variant === 'dark' ? 'bg-positivus-green text-black' : ''}
                     `}>
                       {tool.name}
                     </h3>
                     
                     <div className="flex items-center justify-center">
                        <div className={`
                          w-10 h-10 rounded-full flex items-center justify-center transition-transform group-hover:rotate-45
                          ${variant === 'dark' ? 'bg-white text-black' : 'bg-black text-positivus-green'}
                        `}>
                          <ArrowUpRight size={24} />
                        </div>
                     </div>
                  </div>
                  
                  <div className="flex-1">
                     <span className={`text-base font-medium line-clamp-3
                        ${variant === 'dark' ? 'text-white/90' : 'text-black/80'}
                     `}>
                        {tool.description}
                     </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-6">
            <Search className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">{content.noResultsTitle}</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            {content.noResultsBody} "{searchQuery}"
          </p>
          <button 
            onClick={() => setSearchQuery("")}
            className="mt-6 text-positivus-dark font-bold underline hover:text-positivus-green"
          >
            {content.clearSearch}
          </button>
        </div>
      )}
    </div>
  );
}
